/**
 * /network/:handle — Full-width public profile with suggestions sidebar
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Send, Globe, Youtube, Instagram, Star, Zap, Loader2,
  MessageCircle, Pin, ChevronRight, Users, Trophy, Sparkles, Network,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { leftTopArrowGradient } from '@/assets/svgs/gradientIcons';

const LV_COL   = ['#94a3b8','#7dd3fc','#c4b5fd','#67e8f9','#f0abfc','#62FAE3'];
const LV_LABELS = ['Newcomer','Maker','Creator','Featured','Influencer','Studio Lead'];
const lvCol    = lv => LV_COL[Math.min((lv||1)-1,5)];
const lvLabel  = lv => LV_LABELS[Math.min((lv||1)-1,5)];

const BADGE_LABELS = {
  joined:'🌐 Joined', first_pin:'📌 First Pin', showcase:'🎨 Showcase',
  portfolio:'🔗 Portfolio', storyteller:'📖 Storyteller', producer:'🎬 Producer',
  first_reply:'💬 First Reply', veteran:'⭐ Veteran',
};

function SuggestCard({ profile, onClick }) {
  const col = lvCol(profile.progress?.level);
  return (
    <div onClick={onClick}
      className="group cursor-pointer flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-white/[0.05] transition-all">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs flex-shrink-0 overflow-hidden"
        style={{ background:`linear-gradient(135deg,${col}cc,${col}44)`, color:'#080d14' }}>
        {profile.avatar_url
          ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          : (profile.display_name||'?')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-white truncate">{profile.display_name}</p>
        <p className="text-[10px] text-[#8a94a6]">@{profile.handle} · Lv {profile.progress?.level||1}</p>
      </div>
      <ChevronRight className="w-3 h-3 text-[#8a94a6] group-hover:text-[#62FAE3] transition-colors" />
    </div>
  );
}

export const NetworkPublicProfilePage = () => {
  const { handle }  = useParams();
  const navigate    = useNavigate();
  const [profile, setProfile]     = useState(null);
  const [progress, setProgress]   = useState(null);
  const [pins, setPins]           = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [intro, setIntro]         = useState('');
  const [sending, setSending]     = useState(false);
  const [requested, setRequested] = useState(false);

  const load = useCallback(async () => {
    try {
      const [profRes, dirRes] = await Promise.all([
        api.get(`/network/${handle}`),
        api.get('/network?limit=10').catch(() => ({ data:{ profiles:[] } })),
      ]);
      setProfile(profRes.data.profile);
      setProgress(profRes.data.progress);
      setPins(profRes.data.pins||[]);
      setSuggestions((dirRes.data.profiles||[]).filter(p => p.handle !== handle).slice(0,5));
    } catch { toast.error('Profile not found'); navigate('/network'); }
    finally { setLoading(false); }
  }, [handle, navigate]);

  useEffect(() => { load(); }, [load]);

  const sendRequest = async () => {
    if (!intro.trim()) return toast.error('Add a short intro first');
    setSending(true);
    try {
      await api.post(`/network/${handle}/request`, { intro: intro.trim() });
      toast.success('Message request sent!'); setRequested(true);
    } catch (err) { toast.error(err?.response?.data?.detail||'Request failed'); }
    finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#62FAE3]" />
      </div>
    );
  }
  if (!profile) return null;

  const prog = progress || {};
  const lv   = prog.level || 1;
  const col  = lvCol(lv);
  const init = (profile.display_name || handle)[0]?.toUpperCase();

  return (
    <div className="min-h-screen bg-transparent text-white relative overflow-x-hidden">
      <div className="fixed top-0 left-0 w-[700px] pointer-events-none z-0 opacity-35"
        style={{ transform:'translate(-20%,-20%)' }} dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }} />

      {/* ── Hero banner — full width ── */}
      <div className="relative w-full border-b border-white/[0.06] overflow-hidden">
        {/* Gradient strip */}
        <div className="h-40 w-full relative"
          style={{ background:`linear-gradient(135deg, ${col}20 0%, #1a0f2e 40%, #062018 100%)` }}>
          <div className="absolute inset-0"
            style={{ background:`radial-gradient(ellipse at 30% 50%, ${col}30 0%, transparent 60%), radial-gradient(ellipse at 70% 50%, #BF5AF220 0%, transparent 60%)` }} />
        </div>

        <div className="relative px-5 sm:px-8 pb-5">
          {/* Back button */}
          <button type="button" onClick={() => navigate(-1)}
            className="absolute top-3 left-5 sm:left-8 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm text-[#c5cbd6] hover:text-white text-xs font-medium transition-all active:scale-95 z-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>

          {/* Avatar row */}
          <div className="flex items-end justify-between gap-4 -mt-8">
            <div className="flex items-end gap-4">
              <div className="w-20 h-20 rounded-2xl border-4 border-[#0b0e13] flex items-center justify-center font-black text-2xl overflow-hidden flex-shrink-0"
                style={{ background:`linear-gradient(135deg, ${col}cc, ${col}44)`, color:'#080d14', boxShadow:`0 0 30px ${col}40` }}>
                {profile.avatar_url ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" /> : init}
              </div>
              <div className="mb-1">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-bold text-xs w-fit"
                  style={{ color:col, borderColor:`${col}35`, background:`${col}12` }}>
                  <Star className="w-3 h-3" />Lv {lv} · {lvLabel(lv)}
                </div>
              </div>
            </div>
            {/* DM button shortcut */}
            {profile.accepting_messages && !requested && (
              <Button size="sm" onClick={() => document.getElementById('dm-form')?.scrollIntoView({ behavior:'smooth' })}
                className="mb-1 font-bold text-[#080d14]"
                style={{ background:`linear-gradient(106deg,#D0BCFF,#62FAE3)` }}>
                <MessageCircle className="w-4 h-4 mr-1.5" /> Message
              </Button>
            )}
          </div>

          <div className="mt-2">
            <h1 className="text-2xl sm:text-3xl font-black text-white">{profile.display_name}</h1>
            <p className="text-sm text-[#8a94a6]">@{profile.handle}</p>
            {profile.role && (
              <span className="inline-block mt-1.5 text-xs px-2.5 py-0.5 rounded-full bg-[#D0BCFF]/10 text-[#D0BCFF] border border-[#D0BCFF]/20">
                {profile.role}
              </span>
            )}
          </div>

          {/* Stats row */}
          <div className="flex items-center flex-wrap gap-4 mt-4 pt-3 border-t border-white/[0.07]">
            <div className="flex items-center gap-1.5">
              <Zap className="w-4 h-4" style={{ color:col }} />
              <span className="font-black text-sm" style={{ color:col }}>{(prog.xp||0).toLocaleString()}</span>
              <span className="text-xs text-[#8a94a6]">XP</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Pin className="w-3.5 h-3.5 text-[#8a94a6]" />
              <span className="text-sm text-[#8a94a6]">{pins.length} pin{pins.length!==1?'s':''}</span>
            </div>
            {(prog.badges||[]).map(b => (
              <span key={b.id} className="text-[11px] px-2 py-0.5 rounded-full border border-[#62FAE3]/20 bg-[#62FAE3]/8 text-[#62FAE3]">
                {BADGE_LABELS[b.id]||b.id}
              </span>
            ))}
            {/* Links */}
            {profile.links?.youtube && (
              <a href={profile.links.youtube} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-[#8a94a6] hover:text-red-400 transition-colors">
                <Youtube className="w-3.5 h-3.5" /> YouTube
              </a>
            )}
            {profile.links?.instagram && (
              <a href={profile.links.instagram} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-[#8a94a6] hover:text-[#D0BCFF] transition-colors">
                <Instagram className="w-3.5 h-3.5" /> Instagram
              </a>
            )}
            {profile.links?.portfolio && (
              <a href={profile.links.portfolio} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-[#8a94a6] hover:text-[#62FAE3] transition-colors">
                <Globe className="w-3.5 h-3.5" /> Portfolio
              </a>
            )}
          </div>

          {profile.bio && (
            <p className="text-sm text-[#c5cbd6] mt-3 leading-relaxed max-w-2xl">{profile.bio}</p>
          )}
        </div>
      </div>

      {/* ── Body: pins + DM form / sidebar ── */}
      <div className="flex gap-0 relative z-10">
        {/* Main */}
        <div className="flex-1 min-w-0 p-5 sm:p-8 space-y-6">
          {/* Pinned works */}
          {pins.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <span className="w-1 h-4 rounded-full" style={{ background:'linear-gradient(to bottom,#D0BCFF,#62FAE3)' }} />
                Pinned Work
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {pins.map((p, i) => (
                  <motion.div key={p.id||i} initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.05 }}
                    className="flex items-start gap-2.5 p-3.5 rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#62FAE3]/20 transition-all">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background:`linear-gradient(135deg,${col}18,#62FAE318)`, border:`1px solid ${col}22` }}>
                      <span className="text-sm font-black" style={{ color:col }}>{(p.title||'?')[0]?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{p.title}</p>
                      <p className="text-[10px] text-[#8a94a6] capitalize">{p.source_type}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* DM form */}
          {profile.accepting_messages && (
            <div id="dm-form" className="max-w-lg rounded-2xl border border-[#D0BCFF]/20 bg-white/[0.03] p-5 space-y-3">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#D0BCFF]" />
                Send a Message Request
              </h2>
              {requested ? (
                <div className="py-4 text-center">
                  <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center"
                    style={{ background:'rgba(98,250,227,0.12)', border:'1px solid rgba(98,250,227,0.3)' }}>
                    <span className="text-lg">✓</span>
                  </div>
                  <p className="text-[#62FAE3] font-bold">Request sent!</p>
                  <p className="text-[#8a94a6] text-sm mt-1">You'll be notified when {profile.display_name} accepts.</p>
                </div>
              ) : (
                <>
                  <textarea value={intro} onChange={e => setIntro(e.target.value)} rows={3} maxLength={400}
                    placeholder={`Introduce yourself to ${profile.display_name}…`}
                    className="w-full bg-white/[0.04] border border-white/10 text-white placeholder-[#8a94a6] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#D0BCFF]/40 transition-colors resize-none" />
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] text-[#8a94a6]">{intro.length}/400</span>
                    <Button onClick={sendRequest} disabled={sending||!intro.trim()} className="font-bold text-[#080d14]"
                      style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)', boxShadow:'0 0 14px rgba(208,188,255,0.2)' }}>
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Request
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {!profile.accepting_messages && (
            <div className="max-w-lg p-4 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
              <p className="text-sm text-[#8a94a6]">
                <span className="text-white font-medium">{profile.display_name}</span> is not currently accepting message requests.
              </p>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 flex-shrink-0 p-5 border-l border-white/[0.06]">
          {/* Level progress */}
          {prog.xp !== undefined && (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <Star className="w-4 h-4" style={{ color:col }} /> XP Progress
              </p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-[11px]">
                  <span style={{ color:col }} className="font-bold">{(prog.xp||0).toLocaleString()} XP</span>
                  <span className="text-[#8a94a6]">Lv {lv} · {lvLabel(lv)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: prog.next_level_xp ? `${Math.min(100,Math.round((prog.xp/prog.next_level_xp)*100))}%` : '100%', background:`linear-gradient(90deg,${col},#62FAE3)` }} />
                </div>
              </div>
              {(prog.badges||[]).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1 border-t border-white/[0.07]">
                  {prog.badges.map(b => (
                    <span key={b.id} className="text-[10px] px-1.5 py-0.5 rounded border border-[#62FAE3]/20 bg-[#62FAE3]/8 text-[#62FAE3]">
                      {BADGE_LABELS[b.id]||b.id}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.07] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#62FAE3]" />
                <span className="text-sm font-bold text-white">Similar Creators</span>
              </div>
              <div className="p-2">
                {suggestions.map(p => (
                  <SuggestCard key={p.id||p.handle} profile={p} onClick={() => navigate(`/network/${p.handle}`)} />
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-white/[0.07]">
                <button type="button" onClick={() => navigate('/network')}
                  className="text-xs text-[#62FAE3] hover:underline w-full text-center">Browse all creators →</button>
              </div>
            </div>
          )}

          {/* Quick nav */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-3 space-y-0.5">
            <p className="text-[10px] font-bold text-[#8a94a6] uppercase tracking-wider px-2 pb-1">Navigate</p>
            {[
              { label:'Creator Directory', path:'/network', Icon:Network, col:'#62FAE3' },
              { label:'Leaderboard', path:'/network/leaderboard', Icon:Trophy, col:'#f5c842' },
              { label:'My Profile', path:'/network/me', Icon:Sparkles, col:'#D0BCFF' },
            ].map(({ label, path, Icon, col }) => (
              <button key={path} type="button" onClick={() => navigate(path)}
                className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/[0.05] transition-all text-left">
                <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color:col }} />
                <span className="text-xs text-[#c5cbd6]">{label}</span>
                <ChevronRight className="w-3 h-3 text-[#8a94a6] ml-auto" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkPublicProfilePage;
